import * as yup from 'yup';

const MAX_PROPERTIES = 6;

function requiredIfIncomeSource<T extends yup.AnySchema>(
  schemaType: T,
  incomeSources: string[],
  message: string,
  disableRequired = true,
): T {
  if (disableRequired) return schemaType.notRequired();

  return schemaType.when('incomeSource', {
    is: (incomeSource: string) => incomeSources.includes(incomeSource),
    then: (schema: T) => schema.required(message),
    otherwise: (schema: T) => schema.notRequired(),
  });
}

function requiredIfPR<T extends yup.AnySchema>(
  schemaType: T,
  message: string,
  disableRequired = true,
): T {
  if (disableRequired) return schemaType.notRequired();

  return schemaType.when(['citizenship '], {
    is: (citizenship: string) =>
      citizenship === 'australian_pr_visa' ||
      citizenship === 'australian_tr_visa',
    then: (schema: T) => schema.required(message),
    otherwise: (schema: T) => schema.notRequired(),
  });
}

function requiredIfCoapplicantPR<T extends yup.AnySchema>(
  schemaType: T,
  message: string,
  disableRequired = true,
): T {
  if (disableRequired) return schemaType.notRequired();

  return schemaType.when(['coapplicantCitizenship '], {
    is: (citizenship: string) =>
      citizenship === 'australian_pr_visa' ||
      citizenship === 'australian_tr_visa',
    then: (schema: T) => schema.required(message),
    otherwise: (schema: T) => schema.notRequired(),
  });
}

function requiredIfCoapplicantIncomeSource<T extends yup.AnySchema>(
  schemaType: T,
  incomeSources: string[],
  message: string,
  disableRequired = true,
): T {
  if (disableRequired) return schemaType.notRequired();

  return schemaType.when('coapplicantIncomeSource', {
    is: (incomeSource: string) => incomeSources.includes(incomeSource),
    then: (schema: T) => schema.required(message),
    otherwise: (schema: T) => schema.notRequired(),
  });
}

function requiredIfCondition<T extends yup.AnySchema>(
  schemaType: T,
  fields: string[],
  condition: (...values: any[]) => boolean,
  message: string,
  disableRequired = true,
): T {
  if (disableRequired) return schemaType.notRequired();

  return schemaType.when(fields, {
    is: (...fieldValues: any[]) => condition(...fieldValues),
    then: (schema: T) => schema.required(message),
    otherwise: (schema: T) => schema.notRequired(),
  });
}

type NonPropertyFields = {
  [key: string]: yup.Schema<any>;
};

const nonPropertyFields: NonPropertyFields = {};

for (let i = 1; i <= MAX_PROPERTIES; i++) {
  nonPropertyFields[`nonPropertyLoanType${i}`] = requiredIfCondition(
    yup.string(),
    ['nonPropertyLoan'],
    (nonPropertyLoan) => nonPropertyLoan >= i,
    `Please select non property loan type ${i}`,
  );

  nonPropertyFields[`nonPropertyLoanCurrency${i}`] = requiredIfCondition(
    yup.string(),
    ['nonPropertyLoan'],
    (nonPropertyLoan) => nonPropertyLoan >= i,
    `Please select non property loan currency ${i}`,
  );

  nonPropertyFields[`nonPropertyLoanBalance${i}`] = requiredIfCondition(
    yup.number(),
    ['nonPropertyLoan'],
    (nonPropertyLoan) => nonPropertyLoan >= i,
    `Please enter non property loan balance ${i}`,
  );

  nonPropertyFields[`nonPropertyLoanRepaymentTerm${i}`] = requiredIfCondition(
    yup.number(),
    ['nonPropertyLoan'],
    (nonPropertyLoan) => nonPropertyLoan >= i,
    `Please enter non property loan repayment term ${i}`,
  );

  nonPropertyFields[`nonPropertyLoanRepaymentSum${i}`] = requiredIfCondition(
    yup.number(),
    ['nonPropertyLoan'],
    (nonPropertyLoan) => nonPropertyLoan >= i,
    `Please enter non property loan repayment sum ${i}`,
  );

  nonPropertyFields[`nonPropertyLoanRepaymentFrequency${i}`] =
    requiredIfCondition(
      yup.string(),
      ['nonPropertyLoan'],
      (nonPropertyLoan) => nonPropertyLoan >= i,
      `Please select non property loan repayment frequency ${i}`,
    );
}

type CoapplicantNonPropertyFields = {
  [key: string]: yup.Schema<any>;
};

const coapplicantNonPropertyFields: CoapplicantNonPropertyFields = {};

for (let i = 1; i <= MAX_PROPERTIES; i++) {
  coapplicantNonPropertyFields[`coapplicantNonPropertyLoanType${i}`] =
    requiredIfCondition(
      yup.string(),
      ['coapplicantNonPropertyLoan'],
      (coapplicantNonPropertyLoan) => coapplicantNonPropertyLoan >= i,
      `Please select co-applicant non-property loan type ${i}`,
    );

  coapplicantNonPropertyFields[`coapplicantNonPropertyLoanCurrency${i}`] =
    requiredIfCondition(
      yup.string(),
      ['coapplicantNonPropertyLoan'],
      (coapplicantNonPropertyLoan) => coapplicantNonPropertyLoan >= i,
      `Please select co-applicant non-property loan currency ${i}`,
    );

  coapplicantNonPropertyFields[`coapplicantNonPropertyLoanBalance${i}`] =
    requiredIfCondition(
      yup.number(),
      ['coapplicantNonPropertyLoan'],
      (coapplicantNonPropertyLoan) => coapplicantNonPropertyLoan >= i,
      `Please enter co-applicant non-property loan balance ${i}`,
    );

  coapplicantNonPropertyFields[`coapplicantNonPropertyLoanRepaymentTerm${i}`] =
    requiredIfCondition(
      yup.number(),
      ['coapplicantNonPropertyLoan'],
      (coapplicantNonPropertyLoan) => coapplicantNonPropertyLoan >= i,
      `Please enter co-applicant non-property loan repayment term ${i}`,
    );

  coapplicantNonPropertyFields[`coapplicantNonPropertyLoanRepaymentSum${i}`] =
    requiredIfCondition(
      yup.number(),
      ['coapplicantNonPropertyLoan'],
      (coapplicantNonPropertyLoan) => coapplicantNonPropertyLoan >= i,
      `Please enter co-applicant non-property loan repayment sum ${i}`,
    );

  coapplicantNonPropertyFields[
    `coapplicantNonPropertyLoanRepaymentFrequency${i}`
  ] = requiredIfCondition(
    yup.string(),
    ['coapplicantNonPropertyLoan'],
    (coapplicantNonPropertyLoan) => coapplicantNonPropertyLoan >= i,
    `Please select co-applicant non-property loan repayment frequency ${i}`,
  );
}

export const incomeDetailSchema = yup.object().shape({
  incomeSource: yup.string().required('Please select income source option.'),
  nonPropertyLoan: yup
    .number()
    .required('please verify your non property loan'),
  whereDidYouHearAboutUs: yup
    .string()
    .required('Please select where did you hear about us option.'),
  creditCardCurrency: yup
    .string()
    .required('Please verify your credit card currency'),
  creditCardLimit: yup.number().required('Credit card limit is required'),
  userMessage: yup.string(),

  jobStartMonth: requiredIfIncomeSource(
    yup.string(),
    ['employee'],
    'Please select job start month.',
  ),
  jobStartYear: requiredIfIncomeSource(
    yup.string(),
    ['employee'],
    'Please select job start year.',
  ),
  taxBenefitAwareness: yup.string().required('Please select any option.'),
  occupation: requiredIfIncomeSource(
    yup.string(),
    ['employee'],
    'Occupation is required.',
  ),
  salaryCurrency: requiredIfIncomeSource(
    yup.string(),
    ['employee'],
    'Please select your salary currency.',
  ),
  companySalaryCurrency: requiredIfIncomeSource(
    yup.string(),
    ['self_employed_company', 'self_employed_trader_or_contractor'],
    'Please select your salary currency.',
  ),
  baseSalary: requiredIfIncomeSource(
    yup.number(),
    ['employee'],
    'Please enter your base salary.',
  ),
  salaryFrequency: requiredIfIncomeSource(
    yup.string(),
    ['employee'],
    'Please select your salary frequency.',
  ),
  receiveAllowanceIncome: requiredIfIncomeSource(
    yup.boolean(),
    ['employee'],
    'Please verify your allowance income.',
  ),
  allowanceFrequency: requiredIfCondition(
    yup.string(),
    ['incomeSource', 'receiveAllowanceIncome'],
    (incomeSource, receiveAllowanceIncome) =>
      incomeSource === 'employee' && receiveAllowanceIncome === true,
    'Please select your allowance frequency.',
  ),
  receiveCommissionIncome: requiredIfIncomeSource(
    yup.boolean(),
    ['employee'],
    'Please verify your commission income.',
  ),
  commissionFrequency: yup.string().when('receiveCommissionIncome', {
    is: true,
    then: (schema) =>
      schema.required('Please select your commission frequency'),
    otherwise: (schema) => schema.notRequired(),
  }),
  commissionYears: yup.string().when('receiveCommissionIncome', {
    is: true,
    then: (schema) => schema.required('Please enter your commission years'),
    otherwise: (schema) => schema.notRequired(),
  }),
  receiveAnnualCashBonus: requiredIfIncomeSource(
    yup.boolean(),
    ['employee'],
    'Please verify your annual cash bonus.',
  ),
  receiveStockUnit: requiredIfIncomeSource(
    yup.boolean(),
    ['employee'],
    'Please verify your stock units.',
  ),

  industry: requiredIfIncomeSource(
    yup.string(),
    ['self_employed_company', 'self_employed_trader_or_contractor'],
    'Please select your industry.',
  ),
  operationStartMonth: requiredIfIncomeSource(
    yup.string(),
    ['self_employed_company', 'self_employed_trader_or_contractor'],
    'Please select operation start month.',
  ),
  operationStartYear: requiredIfIncomeSource(
    yup.number(),
    ['self_employed_company', 'self_employed_trader_or_contractor'],
    'Please select operation start year.',
  ),
  thisYearPersonalNetIncomeAfterTaxes: requiredIfIncomeSource(
    yup.number(),
    ['self_employed_company', 'self_employed_trader_or_contractor'],
    'Please enter your personal net income after taxes.',
  ),
  lastYearPersonalNetIncomeAfterTaxes: requiredIfIncomeSource(
    yup.number(),
    ['self_employed_company', 'self_employed_trader_or_contractor'],
    'Please enter your personal net income after taxes.',
  ),

  ownershipPercentage: requiredIfIncomeSource(
    yup.number(),
    ['self_employed_company'],
    'Please enter ownership percentage.',
  ),
  thisYearCompanyNetIncomeAfterTaxes: requiredIfIncomeSource(
    yup.number(),
    ['self_employed_company'],
    'Please enter your company net income after taxes.',
  ),
  lastYearCompanyNetIncomeAfterTaxes: requiredIfIncomeSource(
    yup.number(),
    ['self_employed_company'],
    'Please enter your company net income after taxes.',
  ),

  allowanceIncome: requiredIfCondition(
    yup.number(),
    ['incomeSource', 'receiveAllowanceIncome'],
    (incomeSource, receiveAllowanceIncome) =>
      incomeSource === 'employee' && receiveAllowanceIncome === true,
    'Please enter your allowance income.',
  ),

  typeOfAllowanceIncome: yup
    .array()
    .of(yup.string())
    .when('receiveAllowanceIncome', {
      is: true,
      then: (schema) =>
        schema.required('Please specify the types of allowance income.'),
      otherwise: (schema) => schema.notRequired(),
    }),

  commissionIncome: requiredIfCondition(
    yup.number(),
    ['incomeSource', 'receiveCommissionIncome'],
    (incomeSource, receiveCommissionIncome) =>
      incomeSource === 'employee' && receiveCommissionIncome === true,
    'Please enter your commission income.',
  ),

  lastYearAnnualCashBonus: requiredIfCondition(
    yup.number(),
    ['incomeSource', 'receiveAnnualCashBonus'],
    (incomeSource, receiveAnnualCashBonus) =>
      incomeSource === 'employee' && receiveAnnualCashBonus === true,
    'Please enter your annual cash bonus.',
  ),

  thisYearAnnualCashBonus: requiredIfCondition(
    yup.number(),
    ['incomeSource', 'receiveAnnualCashBonus'],
    (incomeSource, receiveAnnualCashBonus) =>
      incomeSource === 'employee' && receiveAnnualCashBonus === true,
    'Please enter your annual cash bonus.',
  ),

  lastYearStockOptions: requiredIfCondition(
    yup.number(),
    ['incomeSource', 'receiveStockUnit'],
    (incomeSource, receiveStockUnit) =>
      incomeSource === 'employee' && receiveStockUnit === true,
    'Please enter your stock units.',
  ),

  thisYearStockOptions: requiredIfCondition(
    yup.number(),
    ['incomeSource', 'receiveStockUnit'],
    (incomeSource, receiveStockUnit) =>
      incomeSource === 'employee' && receiveStockUnit === true,
    'Please enter your stock units.',
  ),

  coapplicantExists: yup.boolean().required('Please select any option.'),
  coapplicantCreditCardCurrency: requiredIfCoapplicantIncomeSource(
    yup.string(),
    ['employee'],
    'Please verify your credit card currency',
  ),
  coapplicantCreditCardLimit: requiredIfCoapplicantIncomeSource(
    yup.number(),
    ['employee'],
    'Credit card limit is required',
  ),

  coapplicantNonPropertyLoan: requiredIfCoapplicantIncomeSource(
    yup.number(),
    ['employee'],
    'Please select any option.',
  ),

  coapplicantFirstName: requiredIfCondition(
    yup.string(),
    ['coapplicantExists'],
    (coapplicantExists) => coapplicantExists === true,
    'Please enter coaaplicant first name',
  ),

  coapplicantLastName: requiredIfCondition(
    yup.string(),
    ['coapplicantExists'],
    (coapplicantExists) => coapplicantExists === true,
    'Please enter coaaplicant last name',
  ),

  coapplicantEmail: requiredIfCondition(
    yup.string().email('Please enter a valid email address'),
    ['coapplicantExists'],
    (coapplicantExists) => coapplicantExists === true,
    'Please enter coaaplicant email',
  ),

  coapplicantPhone: requiredIfCondition(
    yup.string(),
    ['coapplicantExists'],
    (coapplicantExists) => coapplicantExists === true,
    'Please enter coaaplicant phone',
  ),

  coapplicantRelationship: requiredIfCondition(
    yup.string(),
    ['coapplicantExists'],
    (coapplicantExists) => coapplicantExists === true,
    'Please enter coaaplicant relationship',
  ),

  coapplicantCitizenship: requiredIfCondition(
    yup.string(),
    ['coapplicantExists'],
    (coapplicantExists) => coapplicantExists === true,
    'Please enter coaaplicant citizenship',
  ),

  coapplicantLivingTogether: requiredIfCondition(
    yup.boolean(),
    ['coapplicantExists'],
    (coapplicantExists) => coapplicantExists === true,
    'Please select coaaplicant living together',
  ),

  coapplicantCountry: requiredIfCondition(
    yup.string(),
    ['coapplicantLivingTogether'],
    (coapplicantLivingTogether) => coapplicantLivingTogether === false,
    'Please enter coaaplicant country',
  ),

  coapplicantPermanentResident: requiredIfCondition(
    yup.boolean(),
    ['coapplicantLivingTogether'],
    (coapplicantLivingTogether) => coapplicantLivingTogether === false,
    'Please enter coaaplicant permanent resident',
  ),

  coapplicantCurrentResidentalStatus: requiredIfCondition(
    yup.string(),
    ['coapplicantLivingTogether'],
    (coapplicantLivingTogether) => coapplicantLivingTogether === false,
    'Please enter coaaplicant current residential status',
  ),

  coapplicantRentExpense: requiredIfCondition(
    yup.number(),
    ['coapplicantCurrentResidentialStatus'],
    (coapplicantCurrentResidentialStatus) =>
      coapplicantCurrentResidentialStatus === 'renting',
    'Please enter coaaplicant rent expense',
  ),

  coapplicantFrequencyOfRent: requiredIfCondition(
    yup.string(),
    ['coapplicantCurrentResidentialStatus'],
    (coapplicantCurrentResidentialStatus) =>
      coapplicantCurrentResidentialStatus === 'renting',
    'Please enter coaaplicant frequency of rent',
  ),

  coapplicantIsTheResidentialPlaceMortgaged: requiredIfCondition(
    yup.string(),
    ['coapplicantCurrentResidentialStatus'],
    (coapplicantCurrentResidentialStatus) =>
      coapplicantCurrentResidentialStatus === 'renting',
    'Please enter coaaplicant is the residential place mortgaged',
  ),

  coapplicantResidentalOtherSpecification: requiredIfCondition(
    yup.string(),
    ['coapplicantCurrentResidentialStatus'],
    (coapplicantCurrentResidentialStatus) =>
      coapplicantCurrentResidentialStatus === 'other',
    'Please enter coaaplicant residental other specification',
  ),

  coapplicantIncomeSource: requiredIfCondition(
    yup.string(),
    ['coapplicantExists'],
    (coapplicantExists) => coapplicantExists === true,
    'Please enter coaaplicant income source',
  ),

  coapplicantJobStartMonth: requiredIfCoapplicantIncomeSource(
    yup.string(),
    ['employee'],
    'Please select job start month.',
  ),
  coapplicantJobStartYear: requiredIfCoapplicantIncomeSource(
    yup.string(),
    ['employee'],
    'Please select job start year.',
  ),
  coapplicantOccupation: requiredIfCoapplicantIncomeSource(
    yup.string(),
    ['employee'],
    'Occupation is required.',
  ),
  coapplicantSalaryCurrency: requiredIfCoapplicantIncomeSource(
    yup.string(),
    ['employee'],
    'Please select your salary currency.',
  ),
  coapplicantCompanySalaryCurrency: requiredIfCoapplicantIncomeSource(
    yup.string(),
    ['self_employed_company', 'self_employed_trader_or_contractor'],
    'Please select your company salary currency.',
  ),
  coapplicantBaseSalary: requiredIfCoapplicantIncomeSource(
    yup.number(),
    ['employee'],
    'Please enter your base salary.',
  ),
  coapplicantSalaryFrequency: requiredIfCoapplicantIncomeSource(
    yup.string(),
    ['employee'],
    'Please select your salary frequency.',
  ),
  // coapplicantBaseSalaryFrequency: requiredIfCoapplicantIncomeSource(
  //   yup.string(),
  //   ['employee'],
  //   'Please select your base salary frequency.',
  // ),
  coapplicantReceiveAllowanceIncome: requiredIfCoapplicantIncomeSource(
    yup.boolean(),
    ['employee'],
    'Please verify your allowance income.',
  ),
  coapplicantAllowanceIncome: requiredIfCondition(
    yup.number().typeError('Allowance income must be a number'),
    ['coapplicantIncomeSource', 'coapplicantReceiveAllowanceIncome'],
    (incomeSource, receiveAllowanceIncome) =>
      incomeSource === 'employee' && receiveAllowanceIncome === true,
    'Please enter your allowance income.',
  ),
  coapplicantAllowanceFrequency: requiredIfCondition(
    yup.string(),
    ['coapplicantIncomeSource', 'coapplicantReceiveAllowanceIncome'],
    (incomeSource, receiveAllowanceIncome) =>
      incomeSource === 'employee' && receiveAllowanceIncome === true,
    'Please select your allowance frequency.',
  ),
  coapplicantTypeOfAllowanceIncome: requiredIfCondition(
    yup.array().of(yup.string()),
    ['coapplicantIncomeSource', 'coapplicantReceiveAllowanceIncome'],
    (incomeSource, receiveAllowanceIncome) =>
      incomeSource === 'employee' && receiveAllowanceIncome === true,
    'Please select your allowance type.',
  ),
  coapplicantReceiveCommissionIncome: requiredIfCoapplicantIncomeSource(
    yup.boolean(),
    ['employee'],
    'Please verify your commission income.',
  ),
  coapplicantCommissionIncome: yup
    .number()
    .when('coapplicantReceiveCommissionIncome', {
      is: true,
      then: (schema) =>
        schema.required('Please select your commission frequency'),
      otherwise: (schema) => schema.notRequired(),
    }),
  coapplicantCommissionFrequency: yup
    .string()
    .when('coapplicantReceiveCommissionIncome', {
      is: true,
      then: (schema) =>
        schema.required('Please select your commission frequency'),
      otherwise: (schema) => schema.notRequired(),
    }),
  coapplicantCommissionYears: requiredIfCondition(
    yup.string(),
    ['coapplicantIncomeSource', 'coapplicantReceiveCommissionIncome'],
    (incomeSource, receiveCommissionIncome) =>
      incomeSource === 'employee' && receiveCommissionIncome === true,
    'Please enter your commission years.',
  ),
  coapplicantReceiveAnnualCashBonus: requiredIfCoapplicantIncomeSource(
    yup.boolean(),
    ['employee'],
    'Please verify your annual cash bonus.',
  ),
  coapplicantThisYearAnnualCashBonus: yup
    .number()
    .when('coapplicantReceiveAnnualCashBonus', {
      is: true,
      then: (schema) => schema.required('Please enter your annual cash bonus'),
      otherwise: (schema) => schema.notRequired(),
    }),
  coapplicantLastYearAnnualCashBonus: yup
    .number()
    .when('coapplicantReceiveAnnualCashBonus', {
      is: true,
      then: (schema) => schema.required('Please enter your annual cash bonus'),
      otherwise: (schema) => schema.notRequired(),
    }),
  coapplicantReceiveStockUnit: requiredIfCoapplicantIncomeSource(
    yup.boolean(),
    ['employee'],
    'Please verify your stock units.',
  ),
  coapplicantThisYearStockOptions: yup
    .number()
    .when('coapplicantReceiveStockUnit', {
      is: true,
      then: (schema) => schema.required('Please enter your annual cash bonus'),
      otherwise: (schema) => schema.notRequired(),
    }),
  coapplicantLastYearStockOptions: yup
    .number()
    .when('coapplicantReceiveStockUnit', {
      is: true,
      then: (schema) => schema.required('Please enter your annual cash bonus'),
      otherwise: (schema) => schema.notRequired(),
    }),
  coapplicantIndustry: requiredIfCoapplicantIncomeSource(
    yup.string(),
    ['self_employed_company', 'self_employed_trader_or_contractor'],
    'Please select your industry.',
  ),
  coapplicantOperationStartMonth: requiredIfCoapplicantIncomeSource(
    yup.string(),
    ['self_employed_company', 'self_employed_trader_or_contractor'],
    'Please select operation start month.',
  ),
  coapplicantOperationStartYear: requiredIfCoapplicantIncomeSource(
    yup.number(),
    ['self_employed_company', 'self_employed_trader_or_contractor'],
    'Please select operation start year.',
  ),
  coapplicantThisYearPersonalNetIncomeAfterTaxes:
    requiredIfCoapplicantIncomeSource(
      yup.number(),
      ['self_employed_company', 'self_employed_trader_or_contractor'],
      'Please enter your personal net income after taxes.',
    ),
  coapplicantLastYearPersonalNetIncomeAfterTaxes:
    requiredIfCoapplicantIncomeSource(
      yup.number(),
      ['self_employed_company', 'self_employed_trader_or_contractor'],
      'Please enter your personal net income after taxes.',
    ),

  coapplicantOwnershipPercentage: requiredIfCoapplicantIncomeSource(
    yup.number(),
    ['self_employed_company'],
    'Please enter ownership percentage.',
  ),
  coapplicantThisYearCompanyNetIncomeAfterTaxes:
    requiredIfCoapplicantIncomeSource(
      yup.number(),
      ['self_employed_company'],
      'Please enter your company net income after taxes.',
    ),
  coapplicantLastYearCompanyNetIncomeAfterTaxes:
    requiredIfCoapplicantIncomeSource(
      yup.number(),
      ['self_employed_company'],
      'Please enter your company net income after taxes.',
    ),
  coapplicantVisaSubClass: requiredIfCoapplicantPR(
    yup.string(),
    'Visa sub class is required.',
  ),
  coapplicantVisaExpiryDate: requiredIfCoapplicantPR(
    yup.string(),
    'Visa expire date is required.',
  ),
  ...nonPropertyFields,
  ...coapplicantNonPropertyFields,
});

export const incomeDetailLabelMapper = {
  incomeSource: 'What’s your main source of income?',
  nonPropertyLoan: 'How many non-property loans do you have?',
  nonPropertyLoanType: 'What’s your loan type and other loan info?',
  creditCard: 'What’s your credit details ?',
  userMessage: 'Is there anything you’d want us to know?',
  whereDidYouHearAboutUs: 'Where did you hear about us?',
  coapplicantNonPropertyLoan:
    'How many non-property loans does the co-applicant have?',
};
